import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Phone, MapPin, Clock, ChevronDown, Settings, X, Plus, Trash2, Save, LogOut, Lock, Camera, Truck, Star, UtensilsCrossed, Menu as MenuIcon, Home, Info, Navigation, Mail, PartyPopper } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function FacebookIcon({ size = 20 }) {
  return <svg width={size} height={size} viewBox="0 0 48 48"><linearGradient id="fb1s" x1="9.99" x2="40.62" y1="9.99" y2="40.62" gradientUnits="userSpaceOnUse"><stop offset="0" stopColor="#2aa4f4"/><stop offset="1" stopColor="#007ad9"/></linearGradient><path fill="url(#fb1s)" d="M24 4C12.954 4 4 12.954 4 24s8.954 20 20 20 20-8.954 20-20S35.046 4 24 4z"/><path fill="#fff" d="M26.707 29.301h5.176l.813-5.258h-5.989v-2.874c0-2.184.714-4.121 2.757-4.121H32.8V12.41c-.483-.066-1.509-.217-3.451-.217-4.11 0-6.522 2.162-6.522 7.1v4.749h-4.643v5.258h4.643V44.04c.927.138 1.868.214 2.827.214.894 0 1.77-.066 2.633-.182V29.301z"/></svg>;
}
function GoogleIcon({ size = 20 }) {
  return <svg width={size} height={size} viewBox="0 0 48 48"><path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/><path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/><path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/><path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/></svg>;
}
function InstagramIcon({ size = 20 }) {
  return <svg width={size} height={size} viewBox="0 0 48 48"><radialGradient id="ig1" cx="19.38" cy="42.04" r="44.9" gradientUnits="userSpaceOnUse"><stop offset="0" stopColor="#fd5"/><stop offset=".328" stopColor="#ff543f"/><stop offset=".348" stopColor="#fc5245"/><stop offset=".504" stopColor="#e64771"/><stop offset=".643" stopColor="#d53e91"/><stop offset=".761" stopColor="#cc39a4"/><stop offset=".841" stopColor="#c837ab"/></radialGradient><path fill="url(#ig1)" d="M34.017 41.99l-20 .019c-4.4.004-8.003-3.592-8.008-7.992l-.019-20c-.004-4.4 3.592-8.003 7.992-8.008l20-.019c4.4-.004 8.003 3.592 8.008 7.992l.019 20c.005 4.401-3.592 8.004-7.992 8.008z"/><radialGradient id="ig2" cx="11.79" cy="2.97" r="29.81" gradientUnits="userSpaceOnUse"><stop offset="0" stopColor="#4168c9"/><stop offset=".999" stopColor="#4168c9" stopOpacity="0"/></radialGradient><path fill="url(#ig2)" d="M34.017 41.99l-20 .019c-4.4.004-8.003-3.592-8.008-7.992l-.019-20c-.004-4.4 3.592-8.003 7.992-8.008l20-.019c4.4-.004 8.003 3.592 8.008 7.992l.019 20c.005 4.401-3.592 8.004-7.992 8.008z"/><path fill="#fff" d="M24,31c-3.859,0-7-3.14-7-7s3.141-7,7-7s7,3.14,7,7S27.859,31,24,31z M24,19c-2.757,0-5,2.243-5,5s2.243,5,5,5s5-2.243,5-5S26.757,19,24,19z"/><circle cx="31.5" cy="16.5" r="1.5" fill="#fff"/><path fill="#fff" d="M30,37H18c-3.859,0-7-3.14-7-7V18c0-3.86,3.141-7,7-7h12c3.859,0,7,3.14,7,7v12C37,33.86,33.859,37,30,37z M18,13c-2.757,0-5,2.243-5,5v12c0,2.757,2.243,5,5,5h12c2.757,0,5-2.243,5-5V18c0-2.757-2.243-5-5-5H18z"/></svg>;
}
function TikTokIcon({ size = 20 }) {
  return <svg width={size} height={size} viewBox="0 0 48 48"><path d="M38.4 21.68V16c-2.66 0-4.69-.71-6.33-2.06A10.26 10.26 0 0 1 28 8h-5.34v22.42a5.15 5.15 0 1 1-3.52-4.88v-5.48a10.53 10.53 0 0 0-1.62-.13A10.49 10.49 0 0 0 7.03 30.32a10.49 10.49 0 0 0 10.49 10.49 10.49 10.49 0 0 0 10.49-10.49V19.84A15.25 15.25 0 0 0 38.4 21.68z" fill="#000"/></svg>;
}
function SnapchatIcon({ size = 20 }) {
  return <svg width={size} height={size} viewBox="0 0 48 48"><path fill="#fffc00" d="M24 4C12.954 4 4 12.954 4 24s8.954 20 20 20 20-8.954 20-20S35.046 4 24 4z"/><path fill="#fff" d="M33.5 27.2c-3.2.9-4.6 4.5-4.7 4.7-.2.4-.4.6-1.2.6-.5 0-1-.2-1.6-.4-.5-.2-1-.3-1.5-.3-.4 0-.9.1-1.4.3-.6.2-1.1.4-1.6.4-.8 0-1-.2-1.2-.6-.1-.2-1.5-3.8-4.7-4.7-.5-.1-.8-.5-.7-1 .1-.4.5-.7 1-.7 2.4.3 4.1 1.5 4.1 1.5.4.2.7.1.8-.1.1-.2.1-.5-.2-.8-1.6-1.5-1.7-3.2-1.7-3.8v-3.2c0-3 2.5-5.5 5.5-5.5 3 0 5.5 2.5 5.5 5.5v3.2c0 .6-.1 2.3-1.7 3.8-.3.3-.3.6-.2.8.1.2.4.3.8.1 0 0 1.7-1.2 4.1-1.5.5-.1.9.3 1 .7.1.5-.2.9-.7 1z"/></svg>;
}

const LINK_ICONS = { facebook: FacebookIcon, google: GoogleIcon, instagram: InstagramIcon, tiktok: TikTokIcon, snapchat: SnapchatIcon };

const CATEGORY_ICONS = {
  'Infos & Formules': '📋', 'Les Fromageres': '🧀', 'Les Poissonnieres': '🐟',
  'Les Calzones': '🥟', 'Pizzas Desserts': '🍫', 'Les Classiques': '🍕',
  'Les Speciales': '🌟', 'Les Viandes': '🥩', 'Les Vegetariennes': '🥗',
  'Desserts': '🍰', 'Boissons': '🥤', 'Supplements': '➕'
};

const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } };
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

export default function SitePage() {
  const { slug } = useParams();
  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const menuRef = useRef(null);

  // Edit
  const [editMode, setEditMode] = useState(false);
  const [token, setToken] = useState(() => localStorage.getItem(`menu_token_${slug}`));
  const [showLogin, setShowLogin] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [editData, setEditData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);
  const fileInputRef = useRef(null);
  const [uploadTarget, setUploadTarget] = useState(null);

  const fetchMenu = useCallback(() => {
    axios.get(`${API}/menu/${slug}`)
      .then(res => { setMenu(res.data); setActiveCategory(res.data.categories?.[0]?.name || null); })
      .catch(err => setError(err.response?.data?.detail || 'Page introuvable'))
      .finally(() => setLoading(false));
  }, [slug]);
  useEffect(() => { fetchMenu(); }, [fetchMenu]);

  // Auth handlers
  const handleLogin = async (e) => { e.preventDefault(); setLoginError(''); try { const { data } = await axios.post(`${API}/menu/${slug}/login`, { password }); localStorage.setItem(`menu_token_${slug}`, data.token); setToken(data.token); setShowLogin(false); setPassword(''); setEditData(JSON.parse(JSON.stringify(menu))); setEditMode(true); setShowMenu(true); } catch (err) { setLoginError(err.response?.data?.detail || 'Erreur'); } };
  const handleLogout = () => { localStorage.removeItem(`menu_token_${slug}`); setToken(null); setEditMode(false); setEditData(null); };
  const handleAdminClick = () => { if (token) { setEditData(JSON.parse(JSON.stringify(menu))); setEditMode(true); setShowMenu(true); } else setShowLogin(true); };
  const handleSave = async () => { setSaving(true); try { await axios.put(`${API}/menu/${slug}/update`, { business_name: editData.business_name, subtitle: editData.subtitle, categories: editData.categories }, { headers: { Authorization: `Bearer ${token}` } }); setMenu(editData); setEditMode(false); fetchMenu(); } catch (err) { if (err.response?.status === 401) { handleLogout(); setShowLogin(true); } else alert(err.response?.data?.detail || 'Erreur'); } finally { setSaving(false); } };

  // Item handlers
  const updateItem = (ci, ii, f, v) => { const c = [...editData.categories]; c[ci].items[ii] = { ...c[ci].items[ii], [f]: v }; setEditData({ ...editData, categories: c }); };
  const addItem = (ci) => { const c = [...editData.categories]; c[ci].items.push({ name: 'Nouveau plat', price: 0 }); setEditData({ ...editData, categories: c }); };
  const removeItem = (ci, ii) => { const c = [...editData.categories]; c[ci].items = c[ci].items.filter((_, i) => i !== ii); setEditData({ ...editData, categories: c }); };
  const addCategory = () => { setEditData({ ...editData, categories: [...(editData.categories || []), { name: 'Nouvelle categorie', items: [] }] }); };
  const removeCategory = (ci) => { if (!window.confirm('Supprimer ?')) return; setEditData({ ...editData, categories: editData.categories.filter((_, i) => i !== ci) }); };
  const updateCategoryName = (ci, name) => { const c = [...editData.categories]; c[ci] = { ...c[ci], name }; setEditData({ ...editData, categories: c }); };

  // Upload
  const triggerUpload = (ci, ii) => { setUploadTarget({ ci, ii }); fileInputRef.current?.click(); };
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0]; if (!file || !uploadTarget) return;
    const { ci, ii } = uploadTarget; setUploading(`${ci}-${ii}`);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await axios.post(`${API}/menu/${slug}/upload`, fd, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } });
      const url = data.url.startsWith('http') ? data.url : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      updateItem(ci, ii, 'image', url);
    } catch { alert('Erreur upload'); }
    finally { setUploading(null); setUploadTarget(null); e.target.value = ''; }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ background: '#F5EDE4' }}><div className="w-10 h-10 border-3 border-[#8B6FA3] border-t-transparent rounded-full animate-spin" /></div>;
  if (error) return <div className="min-h-screen flex items-center justify-center" style={{ background: '#F5EDE4' }}><p className="text-[#8B7B6E]">{error}</p></div>;

  const d = editMode ? editData : menu;
  const categories = d?.categories || [];
  const activeItems = categories.find(c => c.name === activeCategory)?.items || [];
  const activeCatIndex = categories.findIndex(c => c.name === activeCategory);
  const formules = categories.find(c => c.name === 'Infos & Formules')?.items || [];
  const menuCategories = categories.filter(c => c.name !== 'Infos & Formules');

  const scrollToMenu = () => { window.open(`/menu/${slug}?digital=1`, '_blank', 'noopener,noreferrer'); };

  const scrollToSection = (id) => {
    setMobileNavOpen(false);
    if (id === 'menu') {
      window.open(`/menu/${slug}?digital=1`, '_blank', 'noopener,noreferrer');
      return;
    }
    if (id === 'qui-paye') {
      window.open(`/qui-paye/${slug}`, '_blank', 'noopener,noreferrer');
      return;
    }
    if (id === 'accueil') { window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const NAV_LINKS = [
    { id: 'accueil', label: 'Accueil', icon: Home },
    { id: 'menu', label: 'Menu', icon: UtensilsCrossed },
    { id: 'histoire', label: 'À propos', icon: Info },
    { id: 'localisation', label: 'Localisation', icon: Navigation },
    { id: 'qui-paye', label: 'Qui paye ?', icon: PartyPopper },
    { id: 'contact', label: 'Contact', icon: Mail },
  ];

  return (
    <div className="min-h-screen" style={{ background: '#F5EDE4' }}>
      <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />

      {/* ═══ NAV ═══ */}
      <nav className="fixed top-0 w-full z-50 backdrop-blur-xl border-b border-[#8B6FA3]/10" style={{ background: 'rgba(245,237,228,0.92)' }}>
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <button onClick={() => scrollToSection('accueil')} className="flex items-center gap-3 active:scale-95 transition-transform" data-testid="nav-brand">
            {d.logo_url && <img src={d.logo_url} alt="" className="w-9 h-9 rounded-full object-cover ring-2 ring-white shadow-sm" />}
            <span className="font-bold text-[#3A2E28] text-base" style={{ fontFamily: "'Playfair Display'" }}>{d.business_name}</span>
          </button>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-1">
            {NAV_LINKS.map(link => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className="px-3 py-1.5 rounded-full text-sm font-medium text-[#3A2E28] hover:bg-[#8B6FA3]/10 hover:text-[#6B4F83] transition-all"
                style={{ fontFamily: 'Outfit' }}
                data-testid={`nav-link-${link.id}`}
              >
                {link.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            {d.phone && <a href={`tel:${d.phone}`} className="hidden lg:flex items-center gap-1.5 text-sm text-[#8B7B6E] hover:text-[#3A2E28] transition-colors"><Phone className="w-3.5 h-3.5" />{d.phone}</a>}
            {editMode ? (
              <div className="flex gap-1.5">
                <button onClick={handleSave} disabled={saving} className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold text-white active:scale-95" style={{ background: '#6B8F71' }}><Save className="w-3 h-3" />{saving ? '...' : 'Sauver'}</button>
                <button onClick={() => { setEditMode(false); setEditData(null); }} className="w-7 h-7 rounded-full flex items-center justify-center bg-[#3A2E28]/5 text-[#8B7B6E]"><X className="w-3.5 h-3.5" /></button>
                <button onClick={handleLogout} className="w-7 h-7 rounded-full flex items-center justify-center bg-red-50 text-red-400"><LogOut className="w-3.5 h-3.5" /></button>
              </div>
            ) : (
              <button onClick={handleAdminClick} className="w-8 h-8 rounded-full flex items-center justify-center bg-[#3A2E28]/5 text-[#8B7B6E] hover:bg-[#3A2E28]/10 transition-all" data-testid="menu-admin-btn"><Settings className="w-3.5 h-3.5" /></button>
            )}
            {/* Mobile burger */}
            <button onClick={() => setMobileNavOpen(v => !v)} className="md:hidden w-8 h-8 rounded-full flex items-center justify-center bg-[#3A2E28]/5 text-[#3A2E28] hover:bg-[#3A2E28]/10" data-testid="nav-mobile-toggle">
              {mobileNavOpen ? <X className="w-4 h-4" /> : <MenuIcon className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Mobile dropdown */}
        {mobileNavOpen && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="md:hidden border-t border-[#8B6FA3]/10" style={{ background: 'rgba(245,237,228,0.98)' }}>
            <div className="max-w-6xl mx-auto px-4 py-3 flex flex-col gap-1">
              {NAV_LINKS.map(link => {
                const Icon = link.icon;
                return (
                  <button
                    key={link.id}
                    onClick={() => scrollToSection(link.id)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-[#3A2E28] hover:bg-[#8B6FA3]/10 transition-all text-left"
                    style={{ fontFamily: 'Outfit' }}
                    data-testid={`nav-mobile-link-${link.id}`}
                  >
                    <Icon className="w-4 h-4 text-[#8B6FA3]" />
                    {link.label}
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </nav>

      {editMode && <div className="fixed top-14 left-0 right-0 z-40 border-b border-[#8B6FA3]/20 px-4 py-2 text-center" style={{ background: '#F0E6F6' }}><p className="text-xs text-[#8B6FA3] font-medium">Mode edition — modifiez puis sauvegardez</p></div>}

      {/* ═══ HERO ═══ */}
      <section id="accueil" className="relative pt-14 overflow-hidden">
        <div className="relative h-[70vh] min-h-[480px] max-h-[600px]">
          <img src="https://static.prod-images.emergentagent.com/jobs/b5f33118-3097-4ba0-a318-31bc1c479f4a/images/5d2a527f1897dd39bec5b7e0cc7c8c7dd45cd1e460196c6852c2c69f301e03e8.png"
            alt="Pizza artisanale" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(58,46,40,0.3) 0%, rgba(58,46,40,0.7) 100%)' }} />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            {d.logo_url && <img src={d.logo_url} alt="" className="w-28 h-28 rounded-full object-cover ring-4 ring-white/30 shadow-2xl mb-5" />}
            <h1 className="text-4xl sm:text-5xl font-black text-white mb-3 drop-shadow-lg" style={{ fontFamily: "'Playfair Display'" }}>{d.business_name}</h1>
            <p className="text-white/80 text-sm sm:text-base max-w-md mx-auto mb-6 leading-relaxed" style={{ fontFamily: 'Outfit' }}>{d.tagline}</p>
            <div className="flex flex-wrap justify-center gap-3">
              <button onClick={scrollToMenu} className="px-7 py-3 rounded-full text-sm font-bold text-white shadow-xl active:scale-95 transition-all" style={{ background: 'linear-gradient(135deg, #8B6FA3, #B794C7)' }}>
                <UtensilsCrossed className="w-4 h-4 inline mr-2" />Voir la carte
              </button>
              {d.phone && <a href={`tel:${d.phone}`} className="px-7 py-3 rounded-full text-sm font-bold text-white bg-white/20 backdrop-blur-sm border border-white/30 active:scale-95 transition-all">
                <Phone className="w-4 h-4 inline mr-2" />Commander
              </a>}
            </div>
          </div>
          <button onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })} className="absolute bottom-6 left-1/2 -translate-x-1/2 animate-bounce">
            <ChevronDown className="w-8 h-8 text-white/60" />
          </button>
        </div>
      </section>

      {/* ═══ INFO STRIP ═══ */}
      <section className="bg-[#3A2E28] py-4">
        <div className="max-w-5xl mx-auto px-4 flex flex-wrap justify-center gap-6 text-center">
          {d.hours && <div className="flex items-center gap-2 text-white/80"><Clock className="w-4 h-4 text-[#B794C7]" /><span className="text-xs" style={{ fontFamily: 'Outfit' }}>{d.hours}</span></div>}
          {d.address && <div className="flex items-center gap-2 text-white/80"><MapPin className="w-4 h-4 text-[#B794C7]" /><span className="text-xs" style={{ fontFamily: 'Outfit' }}>{d.address}</span></div>}
          {d.phone && <div className="flex items-center gap-2 text-white/80"><Phone className="w-4 h-4 text-[#B794C7]" /><span className="text-xs" style={{ fontFamily: 'Outfit' }}>{d.phone}</span></div>}
        </div>
      </section>

      {/* ═══ FORMULES ═══ */}
      {formules.length > 0 && (
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-10">
              <motion.p variants={fadeUp} className="text-xs tracking-[0.2em] uppercase font-bold text-[#8B6FA3] mb-2" style={{ fontFamily: 'Outfit' }}>Nos offres</motion.p>
              <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-black text-[#3A2E28]" style={{ fontFamily: "'Playfair Display'" }}>Les Formules</motion.h2>
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {formules.filter(f => f.name.startsWith('Formule') || f.name === 'Menu Enfant').map((f) => (
                <motion.div key={f.name} variants={fadeUp} className="bg-white rounded-2xl p-6 border border-[#8B6FA3]/10 shadow-sm hover:shadow-lg transition-all text-center group">
                  <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ background: '#F0E6F6' }}>
                    <Star className="w-6 h-6 text-[#8B6FA3]" />
                  </div>
                  <h3 className="text-lg font-bold text-[#3A2E28] mb-2" style={{ fontFamily: "'Playfair Display'" }}>{f.name}</h3>
                  <p className="text-xs text-[#8B7B6E] leading-relaxed mb-3" style={{ fontFamily: 'Outfit' }}>{f.description}</p>
                  {f.price > 0 && <p className="text-2xl font-black text-[#8B6FA3]" style={{ fontFamily: 'Outfit' }}>{f.price.toFixed(2)}€</p>}
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      {/* ═══ LIVRAISON ═══ */}
      {formules.find(f => f.name === 'Livraison') && (
        <section className="py-12 px-4" style={{ background: '#EDE4DA' }}>
          <div className="max-w-3xl mx-auto text-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
              <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#8B6FA3]/10 mb-4">
                <Truck className="w-4 h-4 text-[#8B6FA3]" />
                <span className="text-sm font-bold text-[#8B6FA3]" style={{ fontFamily: 'Outfit' }}>Livraison - {formules.find(f => f.name === 'Livraison')?.price?.toFixed(2)}€</span>
              </motion.div>
              <motion.h2 variants={fadeUp} className="text-2xl font-bold text-[#3A2E28] mb-3" style={{ fontFamily: "'Playfair Display'" }}>On vous livre !</motion.h2>
              <motion.p variants={fadeUp} className="text-sm text-[#8B7B6E] leading-relaxed max-w-lg mx-auto" style={{ fontFamily: 'Outfit' }}>
                {formules.find(f => f.name === 'Livraison')?.description}
              </motion.p>
              {d.phone && <motion.a variants={fadeUp} href={`tel:${d.phone}`} className="inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-full text-sm font-bold text-white active:scale-95 transition-all" style={{ background: 'linear-gradient(135deg, #8B6FA3, #B794C7)' }}>
                <Phone className="w-4 h-4" />Appeler pour commander
              </motion.a>}
            </motion.div>
          </div>
        </section>
      )}

      {/* ═══ MENU ═══ */}
      <section ref={menuRef} className="py-16 px-4" id="menu">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#8B6FA3] mb-2" style={{ fontFamily: 'Outfit' }}>Decouvrez</p>
            <h2 className="text-3xl sm:text-4xl font-black text-[#3A2E28]" style={{ fontFamily: "'Playfair Display'" }}>Notre Carte</h2>
            {d.pricing_info && (
              <div className="mt-5 inline-block px-5 py-3 rounded-2xl border border-[#8B6FA3]/20 shadow-sm" style={{ background: '#F0E6F6' }}>
                <p className="text-xs sm:text-sm font-semibold text-[#6B4F83] tracking-wide" style={{ fontFamily: 'Outfit' }}>{d.pricing_info}</p>
              </div>
            )}
          </div>

          {/* Category tabs */}
          <div className="overflow-x-auto scrollbar-hide mb-8">
            <div className="flex gap-2 justify-center flex-wrap">
              {menuCategories.map(cat => (
                <button key={cat.name} onClick={() => { setActiveCategory(cat.name); setShowMenu(true); }}
                  className={`flex items-center gap-1.5 px-4 py-2.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${activeCategory === cat.name ? 'bg-[#8B6FA3] text-white shadow-md' : 'bg-white text-[#8B7B6E] border border-[#8B6FA3]/15 hover:bg-[#F0E6F6]'}`}
                  style={{ fontFamily: 'Outfit' }} data-testid={`menu-cat-${cat.name}`}>
                  <span className="text-sm">{CATEGORY_ICONS[cat.name] || '🍕'}</span>{cat.name}
                </button>
              ))}
              {editMode && <button onClick={addCategory} className="flex items-center gap-1 px-3 py-2.5 rounded-full text-xs font-medium whitespace-nowrap" style={{ background: '#E8F5E9', color: '#4CAF50' }}><Plus className="w-3 h-3" />Categorie</button>}
            </div>
          </div>

          {/* Items */}
          {(showMenu || editMode) && activeCategory && activeCategory !== 'Infos & Formules' && (
            <motion.div key={activeCategory} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              <div className="flex items-center justify-between mb-5">
                {editMode ? (
                  <input value={activeCategory} onChange={e => { updateCategoryName(activeCatIndex, e.target.value); setActiveCategory(e.target.value); }}
                    className="text-2xl font-bold text-[#3A2E28] bg-transparent border-b-2 border-[#8B6FA3] outline-none flex-1 mr-2" style={{ fontFamily: "'Playfair Display'" }} />
                ) : (
                  <h3 className="text-2xl font-bold text-[#3A2E28] flex items-center gap-2" style={{ fontFamily: "'Playfair Display'" }}>
                    <span>{CATEGORY_ICONS[activeCategory] || '🍕'}</span>{activeCategory}
                  </h3>
                )}
                {editMode && (
                  <div className="flex gap-1.5">
                    <button onClick={() => addItem(activeCatIndex)} className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium" style={{ background: '#E8F5E9', color: '#4CAF50' }}><Plus className="w-3 h-3" />Plat</button>
                    <button onClick={() => removeCategory(activeCatIndex)} className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs" style={{ background: '#FFEBEE', color: '#EF5350' }}><Trash2 className="w-3 h-3" /></button>
                  </div>
                )}
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                {activeItems.map((item, i) => {
                  const hasImage = !!item.image;
                  const isUploading = uploading === `${activeCatIndex}-${i}`;
                  return (
                    <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                      className="bg-white rounded-2xl border border-[#8B6FA3]/8 overflow-hidden shadow-sm hover:shadow-md transition-all">
                      {!editMode && hasImage && <div className="aspect-[16/10] w-full overflow-hidden"><img src={item.image} alt={item.name} className="w-full h-full object-contain bg-[#F5EDE4]" loading="lazy" /></div>}
                      <div className="p-4">
                        {editMode ? (
                          <div className="space-y-2">
                            <input value={item.name} onChange={e => updateItem(activeCatIndex, i, 'name', e.target.value)} className="w-full bg-transparent text-sm font-semibold text-[#3A2E28] border-b border-[#3A2E28]/10 outline-none focus:border-[#8B6FA3]" style={{ fontFamily: "'Playfair Display'" }} placeholder="Nom du plat" />
                            <div className="flex items-center gap-2 flex-wrap">
                              <div className="flex items-center gap-1">
                                <span className="text-[10px] text-[#8B7B6E] uppercase font-bold">Petite</span>
                                <input type="number" step="0.01" value={item.price} onChange={e => updateItem(activeCatIndex, i, 'price', parseFloat(e.target.value) || 0)} className="w-14 bg-transparent text-sm font-bold text-[#8B6FA3] text-right border-b border-[#3A2E28]/10 outline-none focus:border-[#8B6FA3]" />
                                <span className="text-xs text-[#8B7B6E]">€</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <span className="text-[10px] text-[#8B7B6E] uppercase font-bold">Grande</span>
                                <input type="number" step="0.01" value={item.price_large || ''} onChange={e => updateItem(activeCatIndex, i, 'price_large', e.target.value === '' ? null : (parseFloat(e.target.value) || 0))} className="w-14 bg-transparent text-sm font-bold text-[#8B6FA3] text-right border-b border-[#3A2E28]/10 outline-none focus:border-[#8B6FA3]" placeholder="—" />
                                <span className="text-xs text-[#8B7B6E]">€</span>
                              </div>
                              <button onClick={() => removeItem(activeCatIndex, i)} className="ml-auto p-1 rounded hover:bg-red-50 text-red-300 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                            </div>
                            <input value={item.description || ''} onChange={e => updateItem(activeCatIndex, i, 'description', e.target.value)} className="w-full bg-transparent text-xs text-[#8B7B6E] border-b border-[#3A2E28]/10 outline-none focus:border-[#8B6FA3]" style={{ fontFamily: 'Outfit' }} placeholder="Description / Ingrédients" />
                            <div className="flex items-center gap-2">
                              {hasImage && <div className="relative w-12 h-12 rounded-lg overflow-hidden flex-shrink-0"><img src={item.image} alt="" className="w-full h-full object-cover" /><button onClick={() => updateItem(activeCatIndex, i, 'image', '')} className="absolute top-0 right-0 w-4 h-4 rounded-full bg-red-500 text-white flex items-center justify-center"><X className="w-2.5 h-2.5" /></button></div>}
                              <button onClick={() => triggerUpload(activeCatIndex, i)} disabled={isUploading} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-[#8B6FA3]/10 text-[#8B6FA3] hover:bg-[#8B6FA3]/20">
                                {isUploading ? <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
                                {isUploading ? '...' : hasImage ? 'Changer' : 'Photo'}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <div className="flex justify-between items-start gap-2">
                              <h4 className="text-sm font-bold text-[#3A2E28]" style={{ fontFamily: "'Playfair Display'" }}>{item.name}</h4>
                              {item.price > 0 && (
                                <span className="text-sm font-bold text-[#8B6FA3] whitespace-nowrap" style={{ fontFamily: 'Outfit' }}>
                                  {item.price_large ? `${item.price.toFixed(2)}€ / ${item.price_large.toFixed(2)}€` : `${item.price.toFixed(2)}€`}
                                </span>
                              )}
                            </div>
                            {item.description && <p className="text-xs text-[#8B7B6E] mt-1 leading-relaxed" style={{ fontFamily: 'Outfit' }}>{item.description}</p>}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* ═══ À PROPOS / NOTRE HISTOIRE ═══ */}
      <section id="histoire" className="py-16 px-4" style={{ background: '#EDE4DA' }}>
        <div className="max-w-5xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-2 gap-10 items-center">
            <motion.div variants={fadeUp}>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#8B6FA3] mb-2" style={{ fontFamily: 'Outfit' }}>Notre histoire</p>
              <h2 className="text-3xl sm:text-4xl font-black text-[#3A2E28] mb-5" style={{ fontFamily: "'Playfair Display'" }}>
                Une passion pour la pizza authentique
              </h2>
              <p className="text-sm sm:text-base text-[#5B4A40] leading-relaxed mb-4" style={{ fontFamily: 'Outfit' }}>
                Chez <span className="font-bold text-[#3A2E28]">{d.business_name}</span>, chaque pizza est préparée avec soin, dans la pure tradition artisanale.
                Pâte fermentée lentement, ingrédients frais soigneusement sélectionnés, cuisson au four : nous mettons tout notre savoir-faire au service de votre plaisir.
              </p>
              <p className="text-sm sm:text-base text-[#5B4A40] leading-relaxed" style={{ fontFamily: 'Outfit' }}>
                Que ce soit pour un déjeuner rapide, un dîner en famille ou une soirée entre amis, nous vous accueillons avec le sourire et une carte généreuse qui ravira tous les palais.
              </p>
              <div className="mt-6 flex flex-wrap gap-4">
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white shadow-sm">
                  <Star className="w-4 h-4 text-[#8B6FA3]" />
                  <span className="text-xs font-semibold text-[#3A2E28]" style={{ fontFamily: 'Outfit' }}>Recettes traditionnelles</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white shadow-sm">
                  <UtensilsCrossed className="w-4 h-4 text-[#8B6FA3]" />
                  <span className="text-xs font-semibold text-[#3A2E28]" style={{ fontFamily: 'Outfit' }}>Produits frais</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white shadow-sm">
                  <Truck className="w-4 h-4 text-[#8B6FA3]" />
                  <span className="text-xs font-semibold text-[#3A2E28]" style={{ fontFamily: 'Outfit' }}>Livraison rapide</span>
                </div>
              </div>
            </motion.div>
            <motion.div variants={fadeUp} className="relative">
              <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl ring-1 ring-[#8B6FA3]/10">
                <img src="https://static.prod-images.emergentagent.com/jobs/b5f33118-3097-4ba0-a318-31bc1c479f4a/images/5d2a527f1897dd39bec5b7e0cc7c8c7dd45cd1e460196c6852c2c69f301e03e8.png" alt="Notre pizzeria" className="w-full h-full object-cover" />
              </div>
              <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl p-5 shadow-xl border border-[#8B6FA3]/10 max-w-[200px] hidden sm:block">
                <p className="text-3xl font-black text-[#8B6FA3]" style={{ fontFamily: "'Playfair Display'" }}>100%</p>
                <p className="text-xs text-[#8B7B6E] mt-1" style={{ fontFamily: 'Outfit' }}>artisanal & maison</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ═══ LOCALISATION / GOOGLE MAPS ═══ */}
      {d.address && (
        <section id="localisation" className="py-16 px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-10">
              <motion.p variants={fadeUp} className="text-xs tracking-[0.2em] uppercase font-bold text-[#8B6FA3] mb-2" style={{ fontFamily: 'Outfit' }}>Nous trouver</motion.p>
              <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-black text-[#3A2E28]" style={{ fontFamily: "'Playfair Display'" }}>Venez nous rendre visite</motion.h2>
              <motion.p variants={fadeUp} className="text-sm text-[#8B7B6E] mt-3 max-w-lg mx-auto" style={{ fontFamily: 'Outfit' }}>
                {d.address}
              </motion.p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="grid md:grid-cols-3 gap-5">
              {/* Map iframe */}
              <div className="md:col-span-2 rounded-3xl overflow-hidden shadow-xl ring-1 ring-[#8B6FA3]/10 bg-white">
                <iframe
                  title="Carte"
                  src={`https://maps.google.com/maps?q=${encodeURIComponent(d.address)}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
                  width="100%"
                  height="100%"
                  style={{ border: 0, minHeight: '380px' }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  data-testid="site-google-map"
                />
              </div>

              {/* Info cards */}
              <div className="flex flex-col gap-4">
                <div className="bg-white rounded-2xl p-5 shadow-sm border border-[#8B6FA3]/10">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#F0E6F6' }}>
                      <MapPin className="w-5 h-5 text-[#8B6FA3]" />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wider font-bold text-[#8B6FA3] mb-1" style={{ fontFamily: 'Outfit' }}>Adresse</p>
                      <p className="text-sm font-medium text-[#3A2E28] leading-snug" style={{ fontFamily: 'Outfit' }}>{d.address}</p>
                    </div>
                  </div>
                </div>

                {d.hours && (
                  <div className="bg-white rounded-2xl p-5 shadow-sm border border-[#8B6FA3]/10">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#F0E6F6' }}>
                        <Clock className="w-5 h-5 text-[#8B6FA3]" />
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wider font-bold text-[#8B6FA3] mb-1" style={{ fontFamily: 'Outfit' }}>Horaires</p>
                        <p className="text-sm font-medium text-[#3A2E28] leading-snug" style={{ fontFamily: 'Outfit' }}>{d.hours}</p>
                      </div>
                    </div>
                  </div>
                )}

                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(d.address)}`}
                  target="_blank" rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 px-5 py-3 rounded-2xl text-sm font-bold text-white shadow-lg active:scale-95 transition-all"
                  style={{ background: 'linear-gradient(135deg, #8B6FA3, #B794C7)' }}
                  data-testid="site-directions-btn"
                >
                  <Navigation className="w-4 h-4" />
                  Itinéraire
                </a>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* ═══ JEU "QUI PAYE ?" ═══ */}
      <section className="py-14 px-4">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative rounded-3xl overflow-hidden shadow-2xl"
            style={{ background: 'linear-gradient(135deg, #2D1B3D 0%, #4A2D5E 50%, #5B3370 100%)' }}
          >
            {/* Decorative blobs */}
            <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full opacity-20 blur-3xl" style={{ background: '#F59E0B' }} />
            <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full opacity-20 blur-3xl" style={{ background: '#EC4899' }} />

            <div className="relative grid md:grid-cols-5 gap-6 p-8 sm:p-10 items-center">
              <div className="md:col-span-3">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur-sm mb-4 border border-white/20">
                  <PartyPopper className="w-4 h-4 text-amber-300" />
                  <span className="text-[10px] font-bold text-white uppercase tracking-widest" style={{ fontFamily: 'Outfit' }}>Mini-jeu offert</span>
                </div>
                <h2 className="text-4xl sm:text-5xl font-black text-white drop-shadow-lg mb-3 leading-tight" style={{ fontFamily: "'Playfair Display'" }}>
                  Qui Paye <span className="text-amber-300">l&apos;addition ?</span>
                </h2>
                <p className="text-sm sm:text-base text-white/80 leading-relaxed mb-6 max-w-md" style={{ fontFamily: 'Outfit' }}>
                  Entre amis, famille ou collègues ? Entrez vos noms (jusqu&apos;à 8 joueurs) et laissez la roue de la fortune désigner qui régale ce soir 🎰🍕
                </p>
                <a
                  href={`/qui-paye/${slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full text-base font-black text-[#2D1B3D] bg-amber-300 hover:bg-amber-200 shadow-2xl active:scale-95 transition-all"
                  style={{ fontFamily: 'Outfit' }}
                  data-testid="site-qui-paye-cta"
                >
                  <PartyPopper className="w-5 h-5" />
                  Lancer le jeu
                </a>
              </div>

              {/* Visual */}
              <div className="md:col-span-2 hidden md:flex justify-center">
                <div className="relative">
                  <div className="absolute inset-0 rounded-full blur-2xl opacity-50" style={{ background: 'radial-gradient(circle, #FFD700, transparent 70%)' }} />
                  <div className="relative w-44 h-44 rounded-full flex items-center justify-center text-7xl shadow-2xl ring-8 ring-white/10" style={{ background: 'conic-gradient(#8B6FA3 0deg 45deg, #D48C70 45deg 90deg, #6B8F71 90deg 135deg, #E6B981 135deg 180deg, #B794C7 180deg 225deg, #5B9BD5 225deg 270deg, #EC4899 270deg 315deg, #F59E0B 315deg 360deg)' }}>
                    <div className="absolute inset-3 rounded-full bg-[#2D1B3D] flex items-center justify-center">
                      <span className="text-5xl">🎰</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══ FIDELITE & PAIEMENTS ═══ */}
      {(d.loyalty_info || d.payment_methods?.length > 0) && (
        <section className="py-14 px-4">
          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-5">
            {d.loyalty_info && (
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="relative rounded-3xl overflow-hidden p-8 shadow-xl" style={{ background: 'linear-gradient(135deg, #8B6FA3 0%, #B794C7 100%)' }} data-testid="site-loyalty-card">
                <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-white/10" />
                <div className="absolute -bottom-12 -left-12 w-48 h-48 rounded-full bg-white/5" />
                <div className="relative">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-sm mb-4">
                    <Star className="w-4 h-4 text-white" />
                    <span className="text-xs font-bold text-white uppercase tracking-wider" style={{ fontFamily: 'Outfit' }}>Carte de fidélité</span>
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-black text-white mb-3 leading-tight" style={{ fontFamily: "'Playfair Display'" }}>
                    14 pizzas achetées<br/>= 1 petite pizza <span className="underline decoration-white/40">OFFERTE</span>
                  </h3>
                  <p className="text-sm text-white/80 leading-relaxed" style={{ fontFamily: 'Outfit' }}>
                    Demandez votre carte de fidélité dès votre prochaine commande !
                  </p>
                </div>
              </motion.div>
            )}

            {d.payment_methods?.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="rounded-3xl p-8 bg-white border border-[#8B6FA3]/10 shadow-sm" data-testid="site-payment-methods">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F0E6F6] mb-4">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-[#8B6FA3]"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>
                  <span className="text-xs font-bold text-[#8B6FA3] uppercase tracking-wider" style={{ fontFamily: 'Outfit' }}>Moyens de paiement</span>
                </div>
                <h3 className="text-2xl sm:text-3xl font-black text-[#3A2E28] mb-5 leading-tight" style={{ fontFamily: "'Playfair Display'" }}>
                  Paiements acceptés
                </h3>
                <div className="flex flex-wrap gap-2">
                  {d.payment_methods.map((pm) => (
                    <span key={pm} className="px-3 py-2 rounded-xl bg-[#F5EDE4] border border-[#8B6FA3]/10 text-xs sm:text-sm font-semibold text-[#3A2E28]" style={{ fontFamily: 'Outfit' }} data-testid={`payment-method-${pm}`}>
                      {pm}
                    </span>
                  ))}
                </div>
              </motion.div>
            )}
          </div>
        </section>
      )}

      {/* ═══ CONTACT ═══ */}
      <section id="contact" className="py-16 px-4" style={{ background: '#EDE4DA' }}>
        <div className="max-w-4xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-10">
            <motion.p variants={fadeUp} className="text-xs tracking-[0.2em] uppercase font-bold text-[#8B6FA3] mb-2" style={{ fontFamily: 'Outfit' }}>Restons en contact</motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-black text-[#3A2E28]" style={{ fontFamily: "'Playfair Display'" }}>Une question ? Une commande ?</motion.h2>
            <motion.p variants={fadeUp} className="text-sm text-[#8B7B6E] mt-3" style={{ fontFamily: 'Outfit' }}>Nous serons ravis de vous répondre.</motion.p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {d.phone && (
              <motion.a variants={fadeUp} href={`tel:${d.phone}`} className="bg-white rounded-2xl p-6 shadow-sm border border-[#8B6FA3]/10 hover:shadow-lg transition-all text-center group" data-testid="contact-phone-card">
                <div className="w-14 h-14 rounded-full mx-auto mb-3 flex items-center justify-center group-hover:scale-110 transition-transform" style={{ background: '#F0E6F6' }}>
                  <Phone className="w-6 h-6 text-[#8B6FA3]" />
                </div>
                <p className="text-xs uppercase tracking-wider font-bold text-[#8B6FA3] mb-1" style={{ fontFamily: 'Outfit' }}>Téléphone</p>
                <p className="text-base font-bold text-[#3A2E28]" style={{ fontFamily: 'Outfit' }}>{d.phone}</p>
              </motion.a>
            )}
            {d.address && (
              <motion.a variants={fadeUp} href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(d.address)}`} target="_blank" rel="noopener noreferrer" className="bg-white rounded-2xl p-6 shadow-sm border border-[#8B6FA3]/10 hover:shadow-lg transition-all text-center group" data-testid="contact-address-card">
                <div className="w-14 h-14 rounded-full mx-auto mb-3 flex items-center justify-center group-hover:scale-110 transition-transform" style={{ background: '#F0E6F6' }}>
                  <MapPin className="w-6 h-6 text-[#8B6FA3]" />
                </div>
                <p className="text-xs uppercase tracking-wider font-bold text-[#8B6FA3] mb-1" style={{ fontFamily: 'Outfit' }}>Adresse</p>
                <p className="text-sm font-semibold text-[#3A2E28] leading-snug" style={{ fontFamily: 'Outfit' }}>{d.address}</p>
              </motion.a>
            )}
            {d.hours && (
              <motion.div variants={fadeUp} className="bg-white rounded-2xl p-6 shadow-sm border border-[#8B6FA3]/10 text-center">
                <div className="w-14 h-14 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ background: '#F0E6F6' }}>
                  <Clock className="w-6 h-6 text-[#8B6FA3]" />
                </div>
                <p className="text-xs uppercase tracking-wider font-bold text-[#8B6FA3] mb-1" style={{ fontFamily: 'Outfit' }}>Horaires</p>
                <p className="text-sm font-semibold text-[#3A2E28] leading-snug" style={{ fontFamily: 'Outfit' }}>{d.hours}</p>
              </motion.div>
            )}
          </motion.div>

          {/* Reseaux sociaux */}
          {d.links?.length > 0 && (
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="mt-10 text-center">
              <motion.p variants={fadeUp} className="text-xs uppercase tracking-wider font-bold text-[#8B6FA3] mb-4" style={{ fontFamily: 'Outfit' }}>Retrouvez-nous sur les réseaux</motion.p>
              <motion.div variants={fadeUp} className="flex flex-wrap justify-center gap-3">
                {d.links.map((link) => {
                  const IconComp = LINK_ICONS[link.icon];
                  return (
                    <a key={link.url} href={link.url} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-2.5 px-5 py-3 rounded-2xl bg-white border border-[#8B6FA3]/10 shadow-sm hover:shadow-md text-sm font-medium text-[#3A2E28] transition-all active:scale-95"
                      style={{ fontFamily: 'Outfit' }} data-testid={`contact-social-${link.icon}`}>
                      {IconComp ? <IconComp size={22} /> : <Star className="w-5 h-5 text-[#8B6FA3]" />}
                      <span>{link.label}</span>
                    </a>
                  );
                })}
              </motion.div>
            </motion.div>
          )}
        </div>
      </section>

      {/* ═══ CTA ═══ */}
      <section className="py-16 px-4" style={{ background: '#3A2E28' }}>
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-black text-white mb-3" style={{ fontFamily: "'Playfair Display'" }}>Une envie de pizza ?</h2>
          <p className="text-white/60 text-sm mb-6" style={{ fontFamily: 'Outfit' }}>Passez commande par telephone ou venez nous rendre visite !</p>
          <div className="flex flex-wrap justify-center gap-3">
            {d.phone && <a href={`tel:${d.phone}`} className="px-8 py-3.5 rounded-full text-sm font-bold text-white active:scale-95 transition-all shadow-xl" style={{ background: 'linear-gradient(135deg, #8B6FA3, #B794C7)' }}><Phone className="w-4 h-4 inline mr-2" />{d.phone}</a>}
            <button onClick={scrollToMenu} className="px-8 py-3.5 rounded-full text-sm font-bold text-white bg-white/10 border border-white/20 active:scale-95 transition-all"><UtensilsCrossed className="w-4 h-4 inline mr-2" />Voir la carte</button>
          </div>
        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer className="py-10 px-4" style={{ background: '#2D231E' }}>
        <div className="max-w-4xl mx-auto text-center">
          {d.logo_url && <img src={d.logo_url} alt="" className="w-16 h-16 rounded-full mx-auto mb-3 ring-2 ring-white/10 shadow-lg" />}
          <p className="text-base font-bold text-white/90" style={{ fontFamily: "'Playfair Display'" }}>{d.business_name}</p>
          <p className="text-xs text-white/40 mt-1" style={{ fontFamily: 'Outfit' }}>{d.subtitle}</p>
          {d.address && <p className="text-xs text-white/40 mt-2 flex items-center justify-center gap-1" style={{ fontFamily: 'Outfit' }}><MapPin className="w-3 h-3" />{d.address}</p>}
          {d.phone && <p className="text-xs text-white/40 mt-1" style={{ fontFamily: 'Outfit' }}>{d.phone}</p>}
          {d.hours && <p className="text-[10px] text-white/25 mt-3 max-w-sm mx-auto" style={{ fontFamily: 'Outfit' }}>{d.hours}</p>}
          {d.social && <p className="text-xs text-[#B794C7] mt-3 font-medium" style={{ fontFamily: 'Outfit' }}>{d.social}</p>}
          <div className="w-12 h-[1px] mx-auto my-6" style={{ background: 'linear-gradient(to right, transparent, #8B6FA3, transparent)' }} />
          <p className="text-[10px] text-white/20" style={{ fontFamily: 'Outfit' }}>Site par Spin Review</p>
        </div>
      </footer>

      {/* LOGIN MODAL */}
      {showLogin && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
          <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="rounded-3xl border border-[#8B6FA3]/10 p-7 w-full max-w-sm shadow-2xl" style={{ background: '#F5EDE4' }}>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2.5"><Lock className="w-5 h-5 text-[#8B6FA3]" /><h3 className="text-lg font-bold text-[#3A2E28]" style={{ fontFamily: "'Playfair Display'" }}>Espace proprietaire</h3></div>
              <button onClick={() => { setShowLogin(false); setLoginError(''); }} className="text-[#8B7B6E] hover:text-[#3A2E28]"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleLogin} className="space-y-4">
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} autoFocus required
                className="w-full px-4 py-3 rounded-xl bg-white border border-[#3A2E28]/10 text-[#3A2E28] outline-none focus:border-[#8B6FA3] text-sm"
                placeholder="Mot de passe" data-testid="menu-login-password" />
              {loginError && <p className="text-xs text-red-500">{loginError}</p>}
              <button type="submit" className="w-full py-3 rounded-xl text-white font-semibold text-sm active:scale-95 shadow-lg"
                style={{ background: 'linear-gradient(135deg, #8B6FA3, #B794C7)' }} data-testid="menu-login-submit">Se connecter</button>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}
